import { computed } from 'vue'
import { useAuth } from '@/hooks/useAuth'

export function useSubscription() {
  const { user } = useAuth()

  const organization = computed(() => user.value?.organization)

  const subscriptionStatus = computed(() =>
    organization.value?.subscription_status ?? null
  )

  const isActive = computed(() =>
    ['trialing', 'active'].includes(subscriptionStatus.value)
  )

  const isTrialing = computed(() =>
    subscriptionStatus.value === 'trialing'
  )

  const isTrialExpired = computed(() =>
    subscriptionStatus.value === 'trial_expired'
  )

  const isPastDue = computed(() =>
    subscriptionStatus.value === 'past_due'
  )

  const isCancelled = computed(() =>
    subscriptionStatus.value === 'cancelled'
  )

  const isPendingOffer = computed(() =>
    subscriptionStatus.value === 'pending_offer'
  )

  const trialEndsAt = computed(() => {
    const val = organization.value?.trial_ends_at
    return val ? new Date(val) : null
  })

  const trialDaysRemaining = computed(() => {
    if (!trialEndsAt.value) return null
    const now = new Date()
    const diff = trialEndsAt.value.getTime() - now.getTime()
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
  })

  const hasPendingOffer = computed(() =>
    organization.value?.has_pending_offer ?? false
  )

  const needsSubscriptionAction = computed(() =>
    !isActive.value && subscriptionStatus.value !== null
  )

  const billingMode = computed(() =>
    organization.value?.billing_mode ?? 'offers'
  )

  const isPublicMode = computed(() => billingMode.value === 'public')
  const isOffersMode = computed(() => billingMode.value === 'offers')

  return {
    subscriptionStatus,
    isActive,
    isTrialing,
    isTrialExpired,
    isPastDue,
    isCancelled,
    isPendingOffer,
    trialEndsAt,
    trialDaysRemaining,
    hasPendingOffer,
    needsSubscriptionAction,
    billingMode,
    isPublicMode,
    isOffersMode,
  }
}
